import React, { useState } from 'react';
import { Save, FileText, RotateCcw, Sparkles, Calculator } from 'lucide-react';

const parseNum = (val) => {
  if (val === '' || val === null || val === undefined) return null;
  const n = parseFloat(val);
  return isNaN(n) ? null : n;
};

const formatNum = (val, decimals = 3) => {
  if (val === null || val === undefined || isNaN(val)) return '-';
  return Number(val).toFixed(decimals);
};

// Datos reales de ejemplo del archivo CIQU M200 B2 MM PD DIC 2023.xlsx
const excelSampleCargas = [
  {
    tempPatron: '73.2',
    tempMedidor: '73.0',
    flujoBajo: '300',
    flujoAlto: '1750',
    volNominal: '2500',
    lecturaMaster: '2499.8',
    factorMaster: '1.0013',
    factor6B: '0.9939',
    volBrutoMedidor: '2503.000',
    volNetoMedidor: '2488.000'
  },
  {
    tempPatron: '73.4',
    tempMedidor: '73.2',
    flujoBajo: '300',
    flujoAlto: '1750',
    volNominal: '2500',
    lecturaMaster: '2500.1',
    factorMaster: '1.0013',
    factor6B: '0.9938',
    volBrutoMedidor: '2503.500',
    volNetoMedidor: '2488.200'
  },
  {
    tempPatron: '73.5',
    tempMedidor: '73.4',
    flujoBajo: '300',
    flujoAlto: '1750',
    volNominal: '2500',
    lecturaMaster: '2500.0',
    factorMaster: '1.0013',
    factor6B: '0.9938',
    volBrutoMedidor: '2503.700',
    volNetoMedidor: '2488.300'
  },
  {
    tempPatron: '73.6',
    tempMedidor: '73.4',
    flujoBajo: '300',
    flujoAlto: '1750',
    volNominal: '2500',
    lecturaMaster: '2499.9',
    factorMaster: '1.0013',
    factor6B: '0.9937',
    volBrutoMedidor: '2503.600',
    volNetoMedidor: '2488.100'
  },
  {
    tempPatron: '',
    tempMedidor: '',
    flujoBajo: '',
    flujoAlto: '',
    volNominal: '2500',
    lecturaMaster: '',
    factorMaster: '1.0013',
    factor6B: '',
    volBrutoMedidor: '',
    volNetoMedidor: ''
  },
  {
    tempPatron: '',
    tempMedidor: '',
    flujoBajo: '',
    flujoAlto: '',
    volNominal: '2500',
    lecturaMaster: '',
    factorMaster: '1.0013',
    factor6B: '',
    volBrutoMedidor: '',
    volNetoMedidor: ''
  },
  {
    tempPatron: '',
    tempMedidor: '',
    flujoBajo: '',
    flujoAlto: '',
    volNominal: '2500',
    lecturaMaster: '',
    factorMaster: '1.0013',
    factor6B: '',
    volBrutoMedidor: '',
    volNetoMedidor: ''
  },
  {
    tempPatron: '',
    tempMedidor: '',
    flujoBajo: '',
    flujoAlto: '',
    volNominal: '2500',
    lecturaMaster: '',
    factorMaster: '1.0013',
    factor6B: '',
    volBrutoMedidor: '',
    volNetoMedidor: ''
  }
];

const emptyCarga = {
  tempPatron: '',
  tempMedidor: '',
  flujoBajo: '',
  flujoAlto: '',
  volNominal: '2500',
  lecturaMaster: '',
  factorMaster: '1.0013',
  factor6B: '',
  volBrutoMedidor: '',
  volNetoMedidor: ''
};

const FrmRegistroCalibracion = () => {
  const [headerData, setHeaderData] = useState({
    fecha: '2023-12-13',
    planta: 'PLANTA COPEC IQUIQUE',
    brutoInicial: '163868006',
    brutoFinal: '163878998',
    netoInicial: '162754795',
    netoFinal: '162765696',
    compania: 'COPEC S.A.',
    producto: 'PD',
    factorInicial: '0.99806',
    offsetTInicio: '0.5',
    mesaCarga: '200',
    brazo: '2',
    ambientacion: '1000',
    api: '36.3',
    medidorSerie: 'X9823-A',
    masterSerie: 'MM-49102'
  });

  const [cargas, setCargas] = useState(excelSampleCargas);
  const [observaciones, setObservaciones] = useState(
    'Calibración realizada con Master Meter patrón en Mesa 200 Brazo 2. Parámetros dentro de tolerancia.'
  );

  const handleHeaderChange = (e) => {
    setHeaderData({ ...headerData, [e.target.name]: e.target.value });
  };

  const handleCargaChange = (index, field, value) => {
    setCargas((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleResetCargas = () => {
    setCargas(Array(8).fill(null).map(() => ({ ...emptyCarga })));
  };

  const handleLoadExcelSample = () => {
    setCargas(excelSampleCargas.map((c) => ({ ...c })));
  };

  const brutoIni = parseNum(headerData.brutoInicial) || 0;
  const brutoFin = parseNum(headerData.brutoFinal) || 0;
  const netoIni = parseNum(headerData.netoInicial) || 0;
  const netoFin = parseNum(headerData.netoFinal) || 0;
  const totalM3 = brutoFin - brutoIni;
  const totalM3Neto = netoFin - netoIni;

  // Cálculo exacto de las fórmulas del Excel (CIQU M200 B2 MM PD DIC 2023.xlsx)
  const computedCargas = [];
  let currentFactor = parseNum(headerData.factorInicial) || 1;
  let currentOffset = parseNum(headerData.offsetTInicio) || 0;

  for (let i = 0; i < 8; i++) {
    const carga = cargas[i];
    const tPatron = parseNum(carga.tempPatron);
    const tMedidor = parseNum(carga.tempMedidor);
    // Fórmula Excel: =+D13-D14 (Temperatura Patrón - Temperatura Medidor)
    const dif = tPatron !== null && tMedidor !== null ? tPatron - tMedidor : null;

    const lMaster = parseNum(carga.lecturaMaster);
    const fMaster = parseNum(carga.factorMaster);
    // Fórmula Excel: =+D20*D21 (Lectura Master Meter * Factor Master Meter)
    const vMaster = lMaster !== null && fMaster !== null ? lMaster * fMaster : null;

    // Fórmula Excel: =1+((D13-60)*0.0000186) (Corrección Temperatura Pared del Master Meter)
    const cTpared = tPatron !== null ? 1 + (tPatron - 60) * 0.0000186 : null;

    // Fórmula Excel: =TRUNCAR(D22*D23;3) (Volumen Bruto Llenado truncado a 3 decimales)
    const vBrutoLlen =
      vMaster !== null && cTpared !== null ? Math.trunc(vMaster * cTpared * 1000) / 1000 : null;

    const f6B = parseNum(carga.factor6B);
    // Fórmula Excel: =TRUNCAR(D24*D25;3) (Volumen Neto Llenado truncado a 3 decimales)
    const vNetoLlen =
      vBrutoLlen !== null && f6B !== null ? Math.trunc(vBrutoLlen * f6B * 1000) / 1000 : null;

    const vBrutoMed = parseNum(carga.volBrutoMedidor);
    const vNetoMed = parseNum(carga.volNetoMedidor);

    // Fórmula Excel: =+((D24-D28)/D24)*100 (% Error Volumen Bruto)
    const errBruto =
      vBrutoLlen !== null && vBrutoLlen !== 0 && vBrutoMed !== null
        ? ((vBrutoLlen - vBrutoMed) / vBrutoLlen) * 100
        : null;

    // Fórmula Excel: =+((D26-D29)/D26)*100 (% Error Volumen Neto)
    const errNeto =
      vNetoLlen !== null && vNetoLlen !== 0 && vNetoMed !== null
        ? ((vNetoLlen - vNetoMed) / vNetoLlen) * 100
        : null;

    // Fórmula Excel: =+D24-D28 (Diferencia Volumen Bruto)
    const difBruto =
      vBrutoLlen !== null && vBrutoMed !== null ? vBrutoLlen - vBrutoMed : null;

    // Fórmula Excel: =+D26-D29 (Diferencia Volumen Neto)
    const difNeto =
      vNetoLlen !== null && vNetoMed !== null ? vNetoLlen - vNetoMed : null;

    const factorAnterior = currentFactor;
    const offsetAnterior = currentOffset;
    let nuevoFactor = null;
    let nuevoOffset = null;

    // Fórmula Excel Ajuste Nuevo Factor: =SI(O(D31>0.035;D31<-0.035);((D24/D28)*D35);D35)
    if (errBruto !== null && vBrutoMed !== null && vBrutoMed !== 0) {
      if (errBruto > 0.035 || errBruto < -0.035) {
        nuevoFactor = (vBrutoLlen / vBrutoMed) * factorAnterior;
      } else {
        nuevoFactor = factorAnterior;
      }
    }

    // Fórmula Excel Nuevo Ajuste Offset T°: =SI(O(D15>0.8;D15<-0.8);(D15+D37);D37)
    if (dif !== null) {
      if (dif > 0.8 || dif < -0.8) {
        nuevoOffset = dif + offsetAnterior;
      } else {
        nuevoOffset = offsetAnterior;
      }
    }

    if (nuevoFactor !== null) currentFactor = nuevoFactor;
    if (nuevoOffset !== null) currentOffset = nuevoOffset;

    computedCargas.push({
      ...carga,
      dif,
      vMaster,
      cTpared,
      vBrutoLlen,
      vNetoLlen,
      errBruto,
      errNeto,
      difBruto,
      difNeto,
      factorAnterior: errBruto !== null ? factorAnterior : null,
      offsetAnterior: dif !== null ? offsetAnterior : null,
      nuevoFactor,
      nuevoOffset
    });
  }

  const factorFinal = currentFactor;
  const offsetFinal = currentOffset;

  const renderInputRow = ({ label, unit, field, type = 'number', step = 'any', textColor = 'text-slate-800' }) => (
    <tr className="bg-white hover:bg-[#eaf4f8]/60 transition-colors">
      <td className="px-3 py-1.5 text-[12px] font-semibold text-slate-700 border-r border-b border-[#cde0eb] flex items-center justify-between">
        <span>{label}</span>
        {unit && <span className="text-[10px] font-mono text-slate-400 ml-1">{unit}</span>}
      </td>
      {computedCargas.map((c, i) => (
        <td key={i} className="p-0.5 border-r border-b border-[#cde0eb] last:border-r-0">
          <input
            type={type}
            step={step}
            value={c[field]}
            onChange={(e) => handleCargaChange(i, field, e.target.value)}
            className={`w-full text-center text-[13px] py-1 px-1.5 outline-none focus:ring-1 focus:ring-[#0A6ED1] focus:bg-blue-50/40 rounded-xs transition-all bg-transparent font-mono ${textColor}`}
            placeholder="-"
          />
        </td>
      ))}
    </tr>
  );

  const renderComputedRow = ({ label, formulaHint, field, decimals = 3, bg = 'bg-[#eaf4f8]', highlight = false, warnThreshold = null }) => (
    <tr className={`${bg} hover:bg-[#d5e8f2] transition-colors`}>
      <td className="px-3 py-1.5 text-[12px] font-bold text-[#005f88] border-r border-b border-[#cde0eb]">
        <div className="flex items-center justify-between gap-1">
          <span>{label}</span>
          {formulaHint && (
            <span
              className="text-[10px] font-mono bg-[#005f88]/10 text-[#005f88] px-1.5 py-0.5 rounded"
              title={`Fórmula Excel: ${formulaHint}`}
            >
              fx
            </span>
          )}
        </div>
      </td>
      {computedCargas.map((c, i) => {
        const val = c[field];
        const isOut = warnThreshold && val !== null && Math.abs(val) > warnThreshold;
        return (
          <td
            key={i}
            className={`px-2 py-1.5 text-center text-[13px] font-mono border-r border-b border-[#cde0eb] last:border-r-0 ${
              isOut
                ? 'bg-red-100 text-red-700 font-extrabold'
                : highlight
                ? 'text-[#0A6ED1] font-bold'
                : 'text-slate-700 font-semibold'
            }`}
          >
            {formatNum(val, decimals)}
          </td>
        );
      })}
    </tr>
  );

  return (
    <div className="flex flex-col w-full gap-2.5 pb-6">
      {/* Top Action & Toolbar Card (Matching GenericTable Header Bar) */}
      <div className="bg-white p-2.5 rounded shadow-sm border border-slate-200 flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="bg-[#005f88] text-white p-1.5 rounded">
            <Calculator className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-[#005f88] uppercase tracking-wide">
              Hoja de Calibración Master Meter (Fórmulas Excel Activas)
            </h2>
            <p className="text-[11px] text-slate-500">
              Cálculo automático de Corrección T° Pared, TRUNCAR(3), % Error Volumétrico y Ajuste de Factor/Offset
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleLoadExcelSample}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#b8d6e7] bg-white text-[#005f88] hover:bg-[#eaf4f8] rounded text-xs font-bold transition-colors"
            title="Cargar datos del archivo CIQU M200 B2 MM PD DIC 2023.xlsx"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#0A6ED1]" />
            Cargar Ejemplo Excel
          </button>
          <button
            type="button"
            onClick={handleResetCargas}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 rounded text-xs font-bold transition-colors"
            title="Vaciar las 8 corridas"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Limpiar Cargas
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold shadow-sm transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            Emitir Certificado PDF
          </button>
          <button
            type="button"
            onClick={() => alert('Registro de calibración guardado correctamente.')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#006699] hover:bg-[#0854A0] text-white rounded text-xs font-bold shadow-sm transition-colors"
          >
            <Save className="w-3.5 h-3.5" />
            Guardar Registro
          </button>
        </div>
      </div>

      {/* Metadata & KPI Summary Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-2.5">
        {/* Left: Datos de Cabecera */}
        <div className="lg:col-span-8 bg-white rounded shadow-sm border border-[#005f88] overflow-hidden">
          <div className="bg-[#005f88] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-b border-[#004a6b]">
            Datos de Identificación y Numerales del Medidor
          </div>
          <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                Planta / Terminal
              </label>
              <input
                type="text"
                name="planta"
                value={headerData.planta}
                onChange={handleHeaderChange}
                className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded text-slate-800 font-semibold focus:border-[#0A6ED1] outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                Compañía
              </label>
              <input
                type="text"
                name="compania"
                value={headerData.compania}
                onChange={handleHeaderChange}
                className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded text-slate-800 font-semibold focus:border-[#0A6ED1] outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                  Fecha
                </label>
                <input
                  type="date"
                  name="fecha"
                  value={headerData.fecha}
                  onChange={handleHeaderChange}
                  className="w-full px-2 py-1.5 border border-[#b8d6e7] rounded text-slate-800 focus:border-[#0A6ED1] outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                  Producto
                </label>
                <input
                  type="text"
                  name="producto"
                  value={headerData.producto}
                  onChange={handleHeaderChange}
                  className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded text-slate-800 font-bold focus:border-[#0A6ED1] outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                  Mesa
                </label>
                <input
                  type="text"
                  name="mesaCarga"
                  value={headerData.mesaCarga}
                  onChange={handleHeaderChange}
                  className="w-full px-2 py-1.5 border border-[#b8d6e7] rounded text-slate-800 font-mono text-center focus:border-[#0A6ED1] outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                  Brazo
                </label>
                <input
                  type="text"
                  name="brazo"
                  value={headerData.brazo}
                  onChange={handleHeaderChange}
                  className="w-full px-2 py-1.5 border border-[#b8d6e7] rounded text-slate-800 font-mono text-center focus:border-[#0A6ED1] outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                  Amb. (L)
                </label>
                <input
                  type="text"
                  name="ambientacion"
                  value={headerData.ambientacion}
                  onChange={handleHeaderChange}
                  className="w-full px-2 py-1.5 border border-[#b8d6e7] rounded text-slate-800 font-mono text-center focus:border-[#0A6ED1] outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-bold text-[#005f88] uppercase mb-1">
                  Factor Inicial
                </label>
                <input
                  type="number"
                  step="0.00001"
                  name="factorInicial"
                  value={headerData.factorInicial}
                  onChange={handleHeaderChange}
                  className="w-full px-2.5 py-1.5 border border-[#005f88] bg-[#eaf4f8] rounded text-[#005f88] font-mono font-bold focus:border-[#0A6ED1] outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-[#005f88] uppercase mb-1">
                  Offset T° Inicio
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="offsetTInicio"
                  value={headerData.offsetTInicio}
                  onChange={handleHeaderChange}
                  className="w-full px-2.5 py-1.5 border border-[#005f88] bg-[#eaf4f8] rounded text-[#005f88] font-mono font-bold focus:border-[#0A6ED1] outline-none"
                />
              </div>
            </div>

            {/* Numerales Bruto / Neto */}
            <div className="bg-[#eaf4f8]/60 p-2 rounded border border-[#cde0eb]">
              <div className="grid grid-cols-3 gap-1.5 items-center mb-1.5">
                <span className="text-[10px] font-bold text-[#005f88] uppercase">Bruto Ini/Fin</span>
                <input
                  type="number"
                  name="brutoInicial"
                  value={headerData.brutoInicial}
                  onChange={handleHeaderChange}
                  className="bg-white border border-[#b8d6e7] px-1.5 py-1 rounded text-[11px] font-mono text-center"
                />
                <input
                  type="number"
                  name="brutoFinal"
                  value={headerData.brutoFinal}
                  onChange={handleHeaderChange}
                  className="bg-white border border-[#b8d6e7] px-1.5 py-1 rounded text-[11px] font-mono text-center"
                />
              </div>
              <div className="grid grid-cols-3 gap-1.5 items-center">
                <span className="text-[10px] font-bold text-[#005f88] uppercase">Neto Ini/Fin</span>
                <input
                  type="number"
                  name="netoInicial"
                  value={headerData.netoInicial}
                  onChange={handleHeaderChange}
                  className="bg-white border border-[#b8d6e7] px-1.5 py-1 rounded text-[11px] font-mono text-center"
                />
                <input
                  type="number"
                  name="netoFinal"
                  value={headerData.netoFinal}
                  onChange={handleHeaderChange}
                  className="bg-white border border-[#b8d6e7] px-1.5 py-1 rounded text-[11px] font-mono text-center"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right: Resumen Calculado en Tiempo Real */}
        <div className="lg:col-span-4 bg-white rounded shadow-sm border border-[#005f88] overflow-hidden flex flex-col">
          <div className="bg-[#005f88] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-b border-[#004a6b] flex justify-between items-center">
            <span>Resumen de Calibración (Tiempo Real)</span>
            <span className="bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded text-[10px] font-extrabold">
              API: {headerData.api}
            </span>
          </div>
          <div className="p-3 flex-1 grid grid-cols-2 gap-2 bg-[#eaf4f8]/50">
            <div className="bg-white p-2.5 rounded border border-[#cde0eb]">
              <span className="block text-[10px] font-bold text-slate-500 uppercase">
                Totalizador Bruto (L)
              </span>
              <span className="text-lg font-extrabold font-mono text-[#005f88]">
                {totalM3.toLocaleString()}
              </span>
            </div>
            <div className="bg-white p-2.5 rounded border border-[#cde0eb]">
              <span className="block text-[10px] font-bold text-slate-500 uppercase">
                Totalizador Neto (L)
              </span>
              <span className="text-lg font-extrabold font-mono text-emerald-700">
                {totalM3Neto.toLocaleString()}
              </span>
            </div>
            <div className="bg-[#005f88] text-white p-2.5 rounded border border-[#004a6b]">
              <span className="block text-[10px] font-bold text-cyan-200 uppercase">
                Factor Final Calculado
              </span>
              <span className="text-lg font-extrabold font-mono text-amber-300">
                {formatNum(factorFinal, 5)}
              </span>
            </div>
            <div className="bg-[#005f88] text-white p-2.5 rounded border border-[#004a6b]">
              <span className="block text-[10px] font-bold text-cyan-200 uppercase">
                Offset T° Final Calc.
              </span>
              <span className="text-lg font-extrabold font-mono text-amber-300">
                {formatNum(offsetFinal, 2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main 8-Load Calibration Matrix Table */}
      <div className="bg-white rounded shadow-sm border border-[#005f88] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[980px]">
            <thead className="bg-[#005f88] text-white select-none">
              <tr>
                <th className="px-3 py-2 font-bold text-xs uppercase tracking-wider border-r border-[#004a6b] w-64">
                  Parámetro / Corrida de Calibración
                </th>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <th
                    key={i}
                    className="px-2 py-2 text-center font-bold text-xs uppercase tracking-wider border-r border-[#004a6b] last:border-r-0 w-28"
                  >
                    Carga #{i}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* SECCIÓN 1 */}
              <tr className="bg-[#004a6b]/10">
                <td
                  colSpan={9}
                  className="px-3 py-1 text-[11px] font-extrabold text-[#005f88] uppercase tracking-wider border-b border-[#cde0eb]"
                >
                  1. Condiciones de Operación y Temperatura
                </td>
              </tr>
              {renderInputRow({ label: 'Temperatura Patrón', unit: '°F', field: 'tempPatron' })}
              {renderInputRow({ label: 'Temperatura Medidor', unit: '°F', field: 'tempMedidor' })}
              {renderComputedRow({
                label: 'Diferencia T° (Patrón - Medidor)',
                formulaHint: '=+D13-D14',
                field: 'dif',
                decimals: 1,
                highlight: true,
                warnThreshold: 0.8
              })}
              {renderInputRow({ label: 'Flujo Bajo', unit: 'LPM', field: 'flujoBajo', type: 'text' })}
              {renderInputRow({ label: 'Flujo Alto', unit: 'LPM', field: 'flujoAlto', type: 'text' })}
              {renderInputRow({ label: 'Volumen Nominal Patrón', unit: 'L', field: 'volNominal' })}

              {/* SECCIÓN 2 */}
              <tr className="bg-[#004a6b]/10">
                <td
                  colSpan={9}
                  className="px-3 py-1 text-[11px] font-extrabold text-[#005f88] uppercase tracking-wider border-b border-[#cde0eb]"
                >
                  2. Lectura Master Meter (Patrón de Referencia)
                </td>
              </tr>
              {renderInputRow({
                label: 'Lectura Master Meter',
                unit: 'L',
                field: 'lecturaMaster',
                textColor: 'text-[#0A6ED1] font-bold'
              })}
              {renderInputRow({
                label: 'Factor Master Meter',
                field: 'factorMaster',
                textColor: 'text-[#0A6ED1] font-bold'
              })}
              {renderComputedRow({
                label: 'Volumen Master Meter',
                formulaHint: '=+D20*D21',
                field: 'vMaster',
                decimals: 1
              })}
              {renderComputedRow({
                label: 'Corrección T° Pared (CTS)',
                formulaHint: '=1+((D13-60)*0.0000186)',
                field: 'cTpared',
                decimals: 6
              })}
              {renderComputedRow({
                label: 'Volumen Bruto Llenado (Patrón)',
                formulaHint: '=TRUNCAR(D22*D23;3)',
                field: 'vBrutoLlen',
                decimals: 3,
                highlight: true
              })}
              {renderInputRow({
                label: 'Factor de Corrección API 6B (CTL)',
                field: 'factor6B',
                textColor: 'text-[#0A6ED1] font-bold'
              })}
              {renderComputedRow({
                label: 'Volumen Neto Llenado (Patrón)',
                formulaHint: '=TRUNCAR(D24*D25;3)',
                field: 'vNetoLlen',
                decimals: 3,
                highlight: true
              })}

              {/* SECCIÓN 3 */}
              <tr className="bg-[#004a6b]/10">
                <td
                  colSpan={9}
                  className="px-3 py-1 text-[11px] font-extrabold text-[#005f88] uppercase tracking-wider border-b border-[#cde0eb]"
                >
                  3. Lectura Medidor en Prueba (UCL)
                </td>
              </tr>
              {renderInputRow({
                label: 'Volumen Bruto Medidor',
                unit: 'L',
                field: 'volBrutoMedidor',
                textColor: 'text-emerald-700 font-bold'
              })}
              {renderInputRow({
                label: 'Volumen Neto Medidor',
                unit: 'L',
                field: 'volNetoMedidor',
                textColor: 'text-emerald-700 font-bold'
              })}

              {/* SECCIÓN 4 */}
              <tr className="bg-[#004a6b]/10">
                <td
                  colSpan={9}
                  className="px-3 py-1 text-[11px] font-extrabold text-[#005f88] uppercase tracking-wider border-b border-[#cde0eb]"
                >
                  4. Contrastación, Errores y Ajuste Secuencial de Factores
                </td>
              </tr>
              {renderComputedRow({
                label: '% Error Volumen Bruto (Tol ±0.035%)',
                formulaHint: '=+((D24-D28)/D24)*100',
                field: 'errBruto',
                decimals: 3,
                warnThreshold: 0.035
              })}
              {renderComputedRow({
                label: '% Error Volumen Neto',
                formulaHint: '=+((D26-D29)/D26)*100',
                field: 'errNeto',
                decimals: 3
              })}
              {renderComputedRow({
                label: 'Diferencia Volumen Bruto (L)',
                formulaHint: '=+D24-D28',
                field: 'difBruto',
                decimals: 3
              })}
              {renderComputedRow({
                label: 'Diferencia Volumen Neto (L)',
                formulaHint: '=+D26-D29',
                field: 'difNeto',
                decimals: 3
              })}
              {renderComputedRow({
                label: 'Factor Anterior',
                formulaHint: 'Secuencial',
                field: 'factorAnterior',
                decimals: 5,
                bg: 'bg-amber-50/70'
              })}
              {renderComputedRow({
                label: 'Ajuste Nuevo Factor',
                formulaHint: '=SI(O(D31>0.035;D31<-0.035);((D24/D28)*D35);D35)',
                field: 'nuevoFactor',
                decimals: 5,
                bg: 'bg-amber-100/80',
                highlight: true
              })}
              {renderComputedRow({
                label: 'Offset T° Anterior',
                formulaHint: 'Secuencial',
                field: 'offsetAnterior',
                decimals: 1,
                bg: 'bg-amber-50/70'
              })}
              {renderComputedRow({
                label: 'Nuevo Ajuste Offset T°',
                formulaHint: '=SI(O(D15>0.8;D15<-0.8);(D15+D37);D37)',
                field: 'nuevoOffset',
                decimals: 1,
                bg: 'bg-amber-100/80',
                highlight: true
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Observaciones y Firmas */}
      <div className="bg-white rounded shadow-sm border border-[#005f88] p-4">
        <label className="block text-xs font-bold text-[#005f88] uppercase tracking-wider mb-1.5">
          Observaciones Técnicas
        </label>
        <textarea
          rows="2"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          className="w-full border border-[#b8d6e7] rounded p-2 text-xs text-slate-700 focus:border-[#0A6ED1] outline-none bg-[#eaf4f8]/30"
          placeholder="Ingrese observaciones..."
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 pt-4 border-t border-slate-200 text-center">
          <div>
            <div className="w-44 mx-auto border-b border-slate-400 mb-1.5"></div>
            <div className="text-xs font-bold text-slate-800">Luis Morales</div>
            <div className="text-[10px] font-bold text-slate-500 uppercase">Ejecutor 1 - ATECMA</div>
          </div>
          <div>
            <div className="w-44 mx-auto border-b border-slate-400 mb-1.5"></div>
            <div className="text-xs font-bold text-slate-800">Cristian Montecinos</div>
            <div className="text-[10px] font-bold text-slate-500 uppercase">Ejecutor 2 - ATECMA</div>
          </div>
          <div>
            <div className="w-44 mx-auto border-b border-slate-400 mb-1.5"></div>
            <div className="text-xs font-bold text-slate-800">Marcelo Castillo</div>
            <div className="text-[10px] font-bold text-slate-500 uppercase">Supervisor Compañía</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrmRegistroCalibracion;
